import { mayChuNhanSuNenTang } from '../../mock/nhan-su-nen-tang';
import { mockRequest, mockState } from '../../mock/transport';
import type {
  PlatformStaff,
  PlatformStaffCreate,
  PlatformStaffPage,
  PlatformStaffStatus,
  PlatformStaffUpdate,
} from '../../modules/nguoi-dung-nen-tang/types';
import { dungApiThat, goiApi } from '../api/http';

/** Nhóm nhân sự nền tảng của tenant-service (ADR-0057). */
const GOC = '/api/v1/platform-staff';

const duongDan = (id: string, hanhDong?: string) =>
  `${GOC}/${encodeURIComponent(id)}${hanhDong ? `/${hanhDong}` : ''}`;

/**
 * Chạy máy chủ giả **sau** nhịp chờ và các trạng thái `?mock=` của
 * `mockRequest`: lỗi nghiệp vụ tới cùng độ trễ với lỗi thật.
 */
const giaLap = <T>(lam: () => T): Promise<T> => mockRequest(undefined).then(lam);

const TRANG_RONG: PlatformStaffPage = { items: [], next_cursor: null };

export interface ThamSoDanhSach {
  limit: number;
  cursor?: string;
  trang_thai?: PlatformStaffStatus;
}

export const nhanSuNenTangRepository = {
  /**
   * `GET /api/v1/platform-staff` — một trang. `?mock=filtered-empty` diễn ở
   * đây: có bộ lọc thì trả rỗng, nên view phân biệt hai kiểu rỗng bằng chính
   * state bộ lọc của nó, đúng như khi chạy API thật.
   */
  danhSach: ({ limit, cursor, trang_thai }: ThamSoDanhSach): Promise<PlatformStaffPage> => {
    if (dungApiThat()) return goiApi<PlatformStaffPage>(GOC, { query: { limit, cursor, trang_thai } });
    if (mockState() === 'filtered-empty' && trang_thai) return mockRequest(TRANG_RONG);
    return mockRequest(mayChuNhanSuNenTang.danhSach({ limit, cursor, trang_thai }), TRANG_RONG);
  },

  /** `POST /api/v1/platform-staff` — `khoa` là `Idempotency-Key`, do form giữ. */
  deNghi: (du: PlatformStaffCreate, khoa: string): Promise<PlatformStaff> =>
    dungApiThat()
      ? goiApi<PlatformStaff>(GOC, { method: 'POST', body: du, idempotencyKey: khoa })
      : giaLap(() => mayChuNhanSuNenTang.deNghi(du)),

  /** `POST /api/v1/platform-staff/{id}/approve` — bước duyệt của ADR-0057 mục 3. */
  duyet: (id: string): Promise<PlatformStaff> =>
    dungApiThat()
      ? goiApi<PlatformStaff>(duongDan(id, 'approve'), { method: 'POST' })
      : giaLap(() => mayChuNhanSuNenTang.duyet(id)),

  /** `POST /api/v1/platform-staff/{id}/resend-invite` — gửi lại email đặt mật khẩu (ADR-0060 mục 3). */
  guiLai: (id: string): Promise<void> =>
    dungApiThat()
      ? goiApi<void>(duongDan(id, 'resend-invite'), { method: 'POST' })
      : giaLap(() => mayChuNhanSuNenTang.guiLai(id)),

  /** `PATCH /api/v1/platform-staff/{id}` — chỉ gửi trường đổi; `trang_thai` để khoá, mở khoá. */
  sua: (id: string, du: PlatformStaffUpdate): Promise<PlatformStaff> =>
    dungApiThat()
      ? goiApi<PlatformStaff>(duongDan(id), { method: 'PATCH', body: du })
      : giaLap(() => mayChuNhanSuNenTang.sua(id, du)),
};
